import { EntryEditError } from '../edits.ts';
import { javaIsBlank } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection, type SqlRow } from '../sql.ts';
import { stripTags } from '../tables/tableCodec.ts';
import type { ChronologyDate } from '../calendar/chronologyDate.ts';

/**
 * A realm's Story Studio outline (docs/PARITY-STEP-5-PLAN.md section 3), read and written as
 * the desktop's `StoryRepository` and `StoryService` do:
 * - projects are listed by name without regard to case, with their act, chapter and scene counts;
 * - acts, chapters and scenes are kept in order by `position`; a new one goes at the end
 *   (its position is how many siblings it has), and ↑ and ↓ swap positions with a neighbour;
 * - a blank name is "Untitled Project", "Untitled Act", "Untitled Chapter" or "Untitled Scene";
 * - word counts are never stored: they're worked out from the scenes' HTML (`WordCount`);
 * - deleting a project, act or chapter deletes everything inside it (the schema's cascades,
 *   with foreign keys on), with the scenes' links, revisions and the project's writing log.
 */

export interface StoryProject {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** The whole project's word goal; 0 for none. */
  readonly writingGoalWords: number;
  /** Words to write in one sitting; 0 for none. */
  readonly sessionWordGoal: number;
  readonly created: string;
  readonly modified: string;
}

export interface StoryProjectSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly actCount: number;
  readonly chapterCount: number;
  readonly sceneCount: number;
  readonly writingGoalWords: number;
  readonly created: string;
  readonly modified: string;
}

export interface StoryAct {
  readonly id: string;
  readonly projectId: string;
  readonly title: string;
  readonly notes: string;
  readonly position: number;
  readonly created: string;
  readonly modified: string;
}

export interface StoryChapter {
  readonly id: string;
  readonly actId: string;
  readonly title: string;
  readonly notes: string;
  readonly position: number;
  readonly created: string;
  readonly modified: string;
}

/** A scene as the outline shows it: no manuscript, but its word count. */
export interface SceneSummary {
  readonly id: string;
  readonly chapterId: string;
  readonly title: string;
  readonly position: number;
  /** 0 for none. */
  readonly wordGoal: number;
  readonly wordCount: number;
  readonly modified: string;
}

export interface StoryScene extends SceneSummary {
  readonly contentHtml: string;
  readonly storyBeats: string;
  /** Where the scene sits in the realm's Chronology, if anywhere. */
  readonly timelinePosition: ChronologyDate | null;
  readonly entryIds: readonly string[];
  readonly eventIds: readonly string[];
  readonly created: string;
}

export interface ChapterOutline {
  readonly chapter: StoryChapter;
  readonly scenes: readonly SceneSummary[];
}

export interface ActOutline {
  readonly act: StoryAct;
  readonly chapters: readonly ChapterOutline[];
}

export interface StoryOutline {
  readonly project: StoryProject;
  readonly acts: readonly ActOutline[];
  /** Every scene's words together. */
  readonly wordCount: number;
}

/** What goes with a deletion, for its confirmation. */
export interface StoryDeletionImpact {
  readonly acts: number;
  readonly chapters: number;
  readonly scenes: number;
  readonly words: number;
  /** A project's writing log, which goes too. */
  readonly sittings: number;
}

export type StoryLevel = 'project' | 'act' | 'chapter' | 'scene';

export const STORY_LIMITS = {
  name: 200,
  notes: 4000,
  /** A Java `int`, which the desktop reads goals as. */
  goal: 2_147_483_647,
} as const;

const PROJECT_COLUMNS = 'id, name, description, writing_goal_words, session_word_goal, created, modified';
const ACT_COLUMNS = 'id, project_id, title, notes, position, created, modified';
const CHAPTER_COLUMNS = 'id, act_id, title, notes, position, created, modified';
const SCENE_COLUMNS = `s.id, s.chapter_id, s.title, s.content_html, s.story_beats, s.position, s.has_timeline,
  CAST(s.timeline_sort_key AS TEXT) AS timeline_key, s.timeline_label, s.timeline_uncertain, s.word_goal, s.created, s.modified`;

// ---- word counts ----

const JAVA_SPACES = /[ \t\n\x0B\f\r]+/;
const LETTER_OR_DIGIT = /[\p{L}\p{Nd}]/u;

/**
 * The desktop's `WordCount.count`: the text without its tags, split on whitespace, counting
 * only tokens with a letter or digit in them (a link's trailing comma, or a lone dash, isn't a word).
 */
export function wordCount(contentHtml: string): number {
  const plain = stripTags(contentHtml);
  if (javaIsBlank(plain)) return 0;
  return plain.split(JAVA_SPACES).filter((token) => LETTER_OR_DIGIT.test(token)).length;
}

// ---- reading ----

/** Every project, by name without regard to case (`listProjectSummaries`). */
export function listStoryProjects(conn: SqlConnection): StoryProjectSummary[] {
  return conn
    .all(
      `SELECT p.id, p.name, p.description, p.writing_goal_words, p.created, p.modified,
         (SELECT COUNT(*) FROM story_acts a WHERE a.project_id = p.id) AS act_count,
         (SELECT COUNT(*) FROM story_chapters c JOIN story_acts a ON a.id = c.act_id WHERE a.project_id = p.id) AS chapter_count,
         (SELECT COUNT(*) FROM story_scenes s JOIN story_chapters c ON c.id = s.chapter_id JOIN story_acts a ON a.id = c.act_id
            WHERE a.project_id = p.id) AS scene_count
       FROM story_projects p ORDER BY p.name COLLATE NOCASE`,
    )
    .map((row) => ({
      id: text(row['id']),
      name: text(row['name']),
      description: text(row['description']),
      actCount: int(row['act_count']),
      chapterCount: int(row['chapter_count']),
      sceneCount: int(row['scene_count']),
      writingGoalWords: int(row['writing_goal_words']),
      created: text(row['created']),
      modified: text(row['modified']),
    }));
}

export function getStoryProject(conn: SqlConnection, id: string): StoryProject | null {
  const row = conn.all(`SELECT ${PROJECT_COLUMNS} FROM story_projects WHERE id = ?`, [id])[0];
  return row ? projectOf(row) : null;
}

/** The project's acts, chapters and scenes, each level in order, with word counts. */
export function storyOutline(conn: SqlConnection, projectId: string): StoryOutline | null {
  const project = getStoryProject(conn, projectId);
  if (!project) return null;
  let words = 0;
  const acts = listActs(conn, projectId).map((act) => ({
    act,
    chapters: listChapters(conn, act.id).map((chapter) => {
      const scenes = conn.all(`SELECT ${SCENE_COLUMNS} FROM story_scenes s WHERE s.chapter_id = ? ORDER BY s.position`, [chapter.id]).map(sceneSummaryOf);
      for (const scene of scenes) words += scene.wordCount;
      return { chapter, scenes };
    }),
  }));
  return { project, acts, wordCount: words };
}

/** Every scene of the outline in reading order: act, then chapter, then scene (`scenesInOrder`). */
export function scenesInOrder(outline: StoryOutline): SceneSummary[] {
  return outline.acts.flatMap((a) => a.chapters.flatMap((c) => c.scenes));
}

export function listActs(conn: SqlConnection, projectId: string): StoryAct[] {
  return conn.all(`SELECT ${ACT_COLUMNS} FROM story_acts WHERE project_id = ? ORDER BY position`, [projectId]).map(actOf);
}

export function listChapters(conn: SqlConnection, actId: string): StoryChapter[] {
  return conn.all(`SELECT ${CHAPTER_COLUMNS} FROM story_chapters WHERE act_id = ? ORDER BY position`, [actId]).map(chapterOf);
}

export function getAct(conn: SqlConnection, id: string): StoryAct | null {
  const row = conn.all(`SELECT ${ACT_COLUMNS} FROM story_acts WHERE id = ?`, [id])[0];
  return row ? actOf(row) : null;
}

export function getChapter(conn: SqlConnection, id: string): StoryChapter | null {
  const row = conn.all(`SELECT ${CHAPTER_COLUMNS} FROM story_chapters WHERE id = ?`, [id])[0];
  return row ? chapterOf(row) : null;
}

export function getScene(conn: SqlConnection, id: string): StoryScene | null {
  const row = conn.all(`SELECT ${SCENE_COLUMNS} FROM story_scenes s WHERE s.id = ?`, [id])[0];
  if (!row) return null;
  const hasTimeline = int(row['has_timeline']) !== 0;
  return {
    ...sceneSummaryOf(row),
    contentHtml: text(row['content_html']),
    storyBeats: text(row['story_beats']),
    timelinePosition: hasTimeline
      ? { sortKey: BigInt(text(row['timeline_key']) || '0'), label: text(row['timeline_label']), uncertain: int(row['timeline_uncertain']) !== 0 }
      : null,
    entryIds: conn.all('SELECT entry_id FROM story_scene_entries WHERE scene_id = ?', [id]).map((r) => text(r['entry_id'])),
    eventIds: conn.all('SELECT event_id FROM story_scene_events WHERE scene_id = ?', [id]).map((r) => text(r['event_id'])),
    created: text(row['created']),
  };
}

/** What deleting a project, act or chapter takes with it. A scene takes only its own words. */
export function storyDeletionImpact(conn: SqlConnection, level: StoryLevel, id: string): StoryDeletionImpact {
  const scenesWhere = {
    project: 'chapter_id IN (SELECT c.id FROM story_chapters c JOIN story_acts a ON a.id = c.act_id WHERE a.project_id = ?)',
    act: 'chapter_id IN (SELECT id FROM story_chapters WHERE act_id = ?)',
    chapter: 'chapter_id = ?',
    scene: 'id = ?',
  }[level];
  const chapters =
    level === 'project'
      ? int(scalar(conn, 'SELECT COUNT(*) FROM story_chapters c JOIN story_acts a ON a.id = c.act_id WHERE a.project_id = ?', [id]))
      : level === 'act'
        ? int(scalar(conn, 'SELECT COUNT(*) FROM story_chapters WHERE act_id = ?', [id]))
        : 0;
  const html = conn.all(`SELECT content_html FROM story_scenes WHERE ${scenesWhere}`, [id]).map((r) => text(r['content_html']));
  const isProject = level === 'project';
  return {
    acts: isProject ? int(scalar(conn, 'SELECT COUNT(*) FROM story_acts WHERE project_id = ?', [id])) : 0,
    chapters,
    scenes: html.length,
    words: html.reduce((sum, h) => sum + wordCount(h), 0),
    sittings: isProject ? int(scalar(conn, 'SELECT COUNT(*) FROM writing_sessions WHERE project_id = ?', [id])) : 0,
  };
}

// ---- writing: projects ----

export interface StoryProjectInput {
  readonly name: string;
  readonly description: string;
  readonly writingGoalWords: number;
  readonly sessionWordGoal: number;
}

export function createStoryProject(conn: SqlConnection, input: StoryProjectInput, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = cleanProject(input);
  conn.run(
    `INSERT INTO story_projects (name, description, writing_goal_words, session_word_goal, created, modified, id)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [clean.name, clean.description, clean.writingGoalWords, clean.sessionWordGoal, now, now, id],
  );
  return id;
}

export function updateStoryProject(conn: SqlConnection, id: string, input: StoryProjectInput, now: string = new Date().toISOString()): void {
  const clean = cleanProject(input);
  transaction(conn, () => {
    mustExist(conn, 'story_projects', id, 'That story project no longer exists.');
    conn.run('UPDATE story_projects SET name = ?, description = ?, writing_goal_words = ?, session_word_goal = ?, modified = ? WHERE id = ?', [
      clean.name,
      clean.description,
      clean.writingGoalWords,
      clean.sessionWordGoal,
      now,
      id,
    ]);
  });
}

/** Deletes the project with its acts, chapters, scenes and writing log. */
export function deleteStoryProject(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'story_projects', id, 'That story project no longer exists.');
    conn.run('DELETE FROM story_projects WHERE id = ?', [id]);
  });
}

// ---- writing: acts and chapters ----

export function createAct(conn: SqlConnection, projectId: string, title: string, notes: string, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = { title: cleanStoryName(title, 'Untitled Act'), notes: cleanNotes(notes) };
  transaction(conn, () => {
    mustExist(conn, 'story_projects', projectId, 'That story project no longer exists.');
    const position = int(scalar(conn, 'SELECT COUNT(*) FROM story_acts WHERE project_id = ?', [projectId]));
    conn.run('INSERT INTO story_acts (title, notes, position, created, modified, project_id, id) VALUES (?, ?, ?, ?, ?, ?, ?)', [
      clean.title,
      clean.notes,
      position,
      now,
      now,
      projectId,
      id,
    ]);
  });
  return id;
}

export function updateAct(conn: SqlConnection, id: string, title: string, notes: string, now: string = new Date().toISOString()): void {
  const clean = { title: cleanStoryName(title, 'Untitled Act'), notes: cleanNotes(notes) };
  transaction(conn, () => {
    mustExist(conn, 'story_acts', id, 'That act no longer exists.');
    conn.run('UPDATE story_acts SET title = ?, notes = ?, modified = ? WHERE id = ?', [clean.title, clean.notes, now, id]);
  });
}

export function deleteAct(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'story_acts', id, 'That act no longer exists.');
    conn.run('DELETE FROM story_acts WHERE id = ?', [id]);
  });
}

export function createChapter(conn: SqlConnection, actId: string, title: string, notes: string, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = { title: cleanStoryName(title, 'Untitled Chapter'), notes: cleanNotes(notes) };
  transaction(conn, () => {
    mustExist(conn, 'story_acts', actId, 'That act no longer exists.');
    const position = int(scalar(conn, 'SELECT COUNT(*) FROM story_chapters WHERE act_id = ?', [actId]));
    conn.run('INSERT INTO story_chapters (title, notes, position, created, modified, act_id, id) VALUES (?, ?, ?, ?, ?, ?, ?)', [
      clean.title,
      clean.notes,
      position,
      now,
      now,
      actId,
      id,
    ]);
  });
  return id;
}

export function updateChapter(conn: SqlConnection, id: string, title: string, notes: string, now: string = new Date().toISOString()): void {
  const clean = { title: cleanStoryName(title, 'Untitled Chapter'), notes: cleanNotes(notes) };
  transaction(conn, () => {
    mustExist(conn, 'story_chapters', id, 'That chapter no longer exists.');
    conn.run('UPDATE story_chapters SET title = ?, notes = ?, modified = ? WHERE id = ?', [clean.title, clean.notes, now, id]);
  });
}

export function deleteChapter(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'story_chapters', id, 'That chapter no longer exists.');
    conn.run('DELETE FROM story_chapters WHERE id = ?', [id]);
  });
}

// ---- writing: scenes ----

/** A new, empty scene at the end of its chapter, starting with `wordGoal` (0 for none). */
export function createScene(conn: SqlConnection, chapterId: string, title: string, wordGoal = 0, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = { title: cleanStoryName(title, 'Untitled Scene'), wordGoal: cleanGoal(wordGoal) };
  transaction(conn, () => {
    mustExist(conn, 'story_chapters', chapterId, 'That chapter no longer exists.');
    const position = int(scalar(conn, 'SELECT COUNT(*) FROM story_scenes WHERE chapter_id = ?', [chapterId]));
    conn.run(
      `INSERT INTO story_scenes (title, content_html, story_beats, position, has_timeline, timeline_sort_key, timeline_label,
         timeline_uncertain, word_goal, created, modified, chapter_id, id) VALUES (?, '', '', ?, 0, 0, '', 0, ?, ?, ?, ?, ?)`,
      [clean.title, position, clean.wordGoal, now, now, chapterId, id],
    );
  });
  return id;
}

/** A scene's title and word goal (0 clears it). Its manuscript and links are untouched, so no revision is made. */
export function updateSceneOutline(conn: SqlConnection, id: string, title: string, wordGoal: number, now: string = new Date().toISOString()): void {
  const clean = { title: cleanStoryName(title, 'Untitled Scene'), wordGoal: cleanGoal(wordGoal) };
  transaction(conn, () => {
    mustExist(conn, 'story_scenes', id, 'That scene no longer exists.');
    conn.run('UPDATE story_scenes SET title = ?, word_goal = ?, modified = ? WHERE id = ?', [clean.title, clean.wordGoal, now, id]);
  });
}

/** Deletes the scene with its links and revisions. */
export function deleteScene(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'story_scenes', id, 'That scene no longer exists.');
    conn.run('DELETE FROM story_scenes WHERE id = ?', [id]);
  });
}

// ---- moving ----

const SIBLINGS = {
  act: { table: 'story_acts', parent: 'project_id', missing: 'That act no longer exists.' },
  chapter: { table: 'story_chapters', parent: 'act_id', missing: 'That chapter no longer exists.' },
  scene: { table: 'story_scenes', parent: 'chapter_id', missing: 'That scene no longer exists.' },
} as const;

/**
 * Moves an act, chapter or scene one place among its siblings, swapping positions with its
 * neighbour (`swapWithNeighbor`). At either end it stays where it is.
 */
export function moveStoryItem(conn: SqlConnection, level: 'act' | 'chapter' | 'scene', id: string, direction: -1 | 1, now: string = new Date().toISOString()): void {
  const { table, parent, missing } = SIBLINGS[level];
  transaction(conn, () => {
    const parentId = scalar(conn, `SELECT ${parent} FROM ${table} WHERE id = ?`, [id]);
    if (parentId === null) throw new EntryEditError('not_found', missing);
    const siblings = conn.all(`SELECT id, position FROM ${table} WHERE ${parent} = ? ORDER BY position`, [parentId]);
    const index = siblings.findIndex((row) => text(row['id']) === id);
    const neighbour = siblings[index + direction];
    if (index < 0 || !neighbour) return;
    const here = siblings[index]!;
    conn.run(`UPDATE ${table} SET position = ?, modified = ? WHERE id = ?`, [int(neighbour['position']), now, id]);
    conn.run(`UPDATE ${table} SET position = ?, modified = ? WHERE id = ?`, [int(here['position']), now, text(neighbour['id'])]);
  });
}

// ---- rows ----

function projectOf(row: SqlRow): StoryProject {
  return {
    id: text(row['id']),
    name: text(row['name']),
    description: text(row['description']),
    writingGoalWords: Math.max(0, int(row['writing_goal_words'])),
    sessionWordGoal: Math.max(0, int(row['session_word_goal'])),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

function actOf(row: SqlRow): StoryAct {
  return {
    id: text(row['id']),
    projectId: text(row['project_id']),
    title: text(row['title']),
    notes: text(row['notes']),
    position: int(row['position']),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

function chapterOf(row: SqlRow): StoryChapter {
  return {
    id: text(row['id']),
    actId: text(row['act_id']),
    title: text(row['title']),
    notes: text(row['notes']),
    position: int(row['position']),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

function sceneSummaryOf(row: SqlRow): SceneSummary {
  return {
    id: text(row['id']),
    chapterId: text(row['chapter_id']),
    title: text(row['title']),
    position: int(row['position']),
    wordGoal: Math.max(0, int(row['word_goal'])),
    wordCount: wordCount(text(row['content_html'])),
    modified: text(row['modified']),
  };
}

// ---- checks ----

/** As the desktop's constructors: a blank name becomes the default, otherwise it's kept as written (on one line). */
export function cleanStoryName(name: string, fallback: string): string {
  if (name.length > STORY_LIMITS.name) throw new EntryEditError('invalid', `A name can be at most ${STORY_LIMITS.name} characters.`);
  return javaIsBlank(name) ? fallback : name.replace(/[\r\n]+/g, ' ');
}

function cleanNotes(notes: string): string {
  if (notes.length > STORY_LIMITS.notes) throw new EntryEditError('invalid', `Notes can be at most ${STORY_LIMITS.notes.toLocaleString('en')} characters.`);
  return notes;
}

/** A goal is a whole number of words; the desktop keeps it at 0 or more. */
function cleanGoal(goal: number): number {
  if (!Number.isInteger(goal) || goal > STORY_LIMITS.goal) throw new EntryEditError('invalid', 'A word goal must be a whole number of words.');
  return Math.max(0, goal);
}

function cleanProject(input: StoryProjectInput) {
  return {
    name: cleanStoryName(input.name, 'Untitled Project'),
    description: cleanNotes(input.description),
    writingGoalWords: cleanGoal(input.writingGoalWords),
    sessionWordGoal: cleanGoal(input.sessionWordGoal),
  };
}

/** `table` is always one of this module's literals, never input. */
function mustExist(conn: SqlConnection, table: string, id: string, message: string): void {
  if (int(scalar(conn, `SELECT COUNT(*) FROM ${table} WHERE id = ?`, [id])) === 0) throw new EntryEditError('not_found', message);
}
